import type React from "react";
import { Box, Divider, Group, Stack, Text } from "@mantine/core";
import { Badge, Card } from "@xplatform/design-system";
import type { RepairHit } from "@models/repairInformation";
import FieldList from "@components/FieldList";
import ScoreRing from "@components/ScoreRing";
import { similarityColor } from "@utils/score";
import { MODULE_LABELS, factFields } from "./facts";

interface RepairHitCardProps {
  hit: RepairHit;
  /** 1-based position; the answer's citations refer to it. */
  rank: number;
  /** The answer cites this hit. */
  cited?: boolean;
}

/** One posting: title, where it sits in the module tree, and its facts as fields. */
const RepairHitCard: React.FC<RepairHitCardProps> = ({ hit, rank, cited }) => {
  const breakdown =
    `Semantic ${hit.semanticScore.toFixed(2)}, lexical ${hit.lexicalScore.toFixed(2)}, ` +
    `title ${hit.titleScore.toFixed(2)}${hit.routeSignal ? ", routed" : ""}`;
  const facts = hit.facts.map((fact) => factFields(hit.module, fact)).filter((fields) => fields.length > 0);
  const score = Math.round(hit.score);

  return (
    <Card withBorder radius="md" padding="md" shadow="xs" id={`hit-${hit.key}`}>
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
        <Group wrap="nowrap" align="flex-start" gap="sm">
          <Text size="sm" fw={700} c="dimmed" w={24} ta="right" mt={26}>
            {rank}
          </Text>
          <Stack gap={6}>
            {/* Where the hit sits: module and its path in the module tree. */}
            <Group gap={6}>
              <Badge size="sm" variant="light" color="brand" tt="none" maw="100%">
                {[MODULE_LABELS[hit.module] ?? hit.module, ...hit.breadcrumb].join(" › ")}
              </Badge>
              {cited && (
                <Badge size="sm" variant="light" color="teal">
                  Cited
                </Badge>
              )}
            </Group>
            <Text fw={600}>{hit.title}</Text>
          </Stack>
        </Group>
        <ScoreRing value={score} label={String(score)} color={similarityColor(score)} hint={breakdown} />
      </Group>

      {facts.length > 0 && (
        <Stack gap={6} mt="sm" pl={36}>
          {facts.map((fields, index) => (
            <Box key={index}>
              {index > 0 && <Divider variant="dashed" mb={6} />}
              <FieldList fields={fields} />
            </Box>
          ))}
        </Stack>
      )}
    </Card>
  );
};

export default RepairHitCard;
